import { el, mount, unmount } from 'redom';

import { isEqual } from 'lodash';
import { util } from 'fabric';

import OutsideClickEventRegistration from 'lib/outside-click-event-registration';

import {
  displayElementAroundAnchor
} from 'utilities/element-position';

import PlayerControls from './controls';
import PlayerTimeline from './timeline';
import PlayerDuration from './duration';
import Tracks from './tracks';
import ProgressDisplay from './progress-display';

import {
  determineTrackName
} from './utilities';

import './index.scss';

class VideoBuilder {
  constructor(element, canvas, updateProperties, container) {

    this.element = structuredClone(element);
    this.canvas = canvas;
    this.hideTimer = null;
    this.displayed = false;
    this.outsideEditInputClickEventRegistration = null;
    this.recordingProgressDisplay = null;

    this.playerProperties = {
      durationInSeconds: this.element.properties.player.durationInSeconds,
      tracks: [...this.element.properties.player.tracks],
    };
    this.updateProperties = updateProperties;
    this.container = container;

    this.canvas.addEventListener('added', (e) => {
      this.playerProperties.tracks.push({
        id: e.detail.id,
        type: e.detail.type,
        start: 0,
        duration: this.playerProperties.durationInSeconds,
        name: determineTrackName(this.playerProperties.tracks, e.detail)
      });
      this.tracks.update(this.playerProperties.tracks);
      this.updateProperties({ player: structuredClone(this.playerProperties) });
    });
    this.canvas.addEventListener('removed', (e) => {
      this.playerProperties.tracks = this.playerProperties.tracks.filter(
        (track) => track.id !== e.detail.id
      );
      this.tracks.update(this.playerProperties.tracks);
      this.updateProperties({ player: structuredClone(this.playerProperties) });
    });

    this.playerControls = new PlayerControls(this);
    this.playerTimeline = new PlayerTimeline(this);
    this.playerDuration = new PlayerDuration(this);
    this.tracks = new Tracks(this);

    this.timeline = null;
    this.startTime = null;
    this.pausedAt = null;
    this.isPlaying = false;
    this.animationFrameId = null;

    this.el = el('.video-builder-main.hide-scrollbar', {
      // onmouseenter: () => {
      //   clearTimeout(this.hideTimer);
      // },
      // onmouseleave: () => {
      //   this.hide();
      // }
    }, [
      this.renderPlayerControls(),
      this.renderTracks(),
    ]);

    this.playerControls.play();

  }

  renderPlayerControls() {
    return el('.video-builder-player-controls-container', [
      this.playerControls,
      this.playerTimeline,
      this.playerDuration,
    ]);
  }

  renderTracks() {
    return this.tracks;
  }

  updateControls(timeElapsed) {

    const percentage = Math.floor(((timeElapsed / this.playerProperties.durationInSeconds) * 100));

    this.playerTimeline.update(timeElapsed);
    this.playerDuration.update(timeElapsed);

    if (this.recordingProgressDisplay !== null) {
      this.recordingProgressDisplay.update(percentage);
    }

  }

  updateObjectVisibility(track, visible, play = true) {
    const canvas = this.canvas.fabricCanvas;
    const object = canvas.getObjects().find((obj) => obj.id === track.id);
    if (!object) return;

    object.visible = visible;
    if (object.type === 'video') {
      if (visible && object.videoElement) {
        if (play === true) {
          object.videoElement.play();
        }
      } else if (!visible && object.videoElement) {
        try {

          object.videoElement.pause();
          object.videoElement.currentTime = 0; // Reset playback position

        } catch (exception) {

          console.log(exception);

        }
      }
    }
    if (object.type === 'audio') {
      object.visible = play === false;
      if (visible) {
        if (play === true) {
          object.play();
        }
      } else if (!visible) {
        try {

          object.stop();
        } catch (exception) {

          console.log(exception);

        }
      }
    }
    canvas.requestRenderAll();

  }

  animationLoop(timestamp) {

    if (!this.startTime) this.startTime = timestamp;

    let elapsedTime = (timestamp - this.startTime) / 1000;

    if (elapsedTime >= this.playerProperties.durationInSeconds) {
      this.stop();
      return;
    }

    this.playerProperties.tracks.forEach((track) => {
      const shouldShow =
        elapsedTime >= track.start &&
        elapsedTime <= track.start + track.duration;
      this.updateObjectVisibility(track, shouldShow);
    });

    this.updateControls(elapsedTime);
    this.animationFrameId = util.requestAnimFrame(this.animationLoop.bind(this));
  }

  play() {
    if (this.isPlaying) return;

    this.isPlaying = true;
    this.startTime = performance.now();
    this.animationFrameId = util.requestAnimFrame(this.animationLoop.bind(this));
  }

  pause() {
    if (!this.isPlaying) return;

    this.isPlaying = false;
    this.pausedAt = (performance.now() - this.startTime) / 1000; // Save elapsed time
    util.cancelAnimFrame(this.animationFrameId);

    this.playerProperties.tracks.forEach((track) => {
      const object = this.canvas.fabricCanvas
        .getObjects()
        .find((obj) => obj.id === track.id);
      if (object) {
        if (object.visible && object.videoElement) {
          try {

            object.videoElement.pause();

          } catch (exception) {}
        }
        if (object.audioElement) {
          try {

            object.pause();

          } catch (exception) {}
        }
      }
    });
  }

  resume() {

    this.startTime = performance.now() - (this.pausedAt * 1000);

    this.isPlaying = true;
    this.animationFrameId = util.requestAnimFrame(this.animationLoop.bind(this));

  }

  stop() {

    this.isPlaying = false;
    this.startTime = null;
    this.pausedAt = null;

    util.cancelAnimFrame(this.animationFrameId);

    this.playerProperties.tracks.forEach((track) => this.updateObjectVisibility(track, false));
    this.updateControls(0);
    this.playerControls.reset();
    this.playerProperties.tracks.forEach((track) => this.updateObjectVisibility(track, true, false));

    if (typeof this.onEnd === 'function') {
      this.onEnd();
    }

  }

  restart() {
    this.stop();
    this.play();
  }

  seek(positionInSeconds) {
    const { durationInSeconds, tracks } = this.playerProperties;

    // Clamp position to valid range
    const clampedPosition = Math.max(0, Math.min(positionInSeconds, durationInSeconds));

    // Update startTime to reflect the new position
    this.startTime = performance.now() - clampedPosition * 1000;
    this.pausedAt = clampedPosition;

    // Update the visibility and playback state of tracks
    tracks.forEach((track) => {
      const shouldShow =
        clampedPosition >= track.start &&
        clampedPosition <= track.start + track.duration;

      this.updateObjectVisibility(track, shouldShow);

      // Adjust media playback if necessary
      if (shouldShow && track.type === 'video') {
        const object = this.canvas.fabricCanvas
          .getObjects()
          .find((obj) => obj.id === track.id);
        if (object && object.videoElement) {
          object.videoElement.currentTime = clampedPosition - track.start;
          if (this.isPlaying) {
            object.videoElement.play();
          } else {
            try {

              object.videoElement.pause();

            } catch (exception) {

            }
          }
        }
      }
    });

    // Update UI controls
    this.updateControls(clampedPosition);
  }

  show(anchorElement, container) {

    if (this.displayed === true) {

      clearTimeout(this.hideTimer);
      return;
    }
    this.displayed = true;

    this.el.style.display = 'flex';
    const element = this.el;
    // const anchorElement = this.el.parentNode;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'left';
    const placeOnCenter = true;
    const positionableSides = ['left', 'bottom'];

    this.el.style.width = container.offsetWidth + 'px';

    const result = displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

    if (result === false) {
      displayElementAroundAnchor(element, anchorElement, viewportElement, -200, horizontalOffset, startSide, placeOnCenter, positionableSides);
    }

    this.el.style.height = 'auto';

    this.outsideEditInputClickEventRegistration = new OutsideClickEventRegistration(this.el.parentNode, this.hideNow.bind(this));

  }

  hideNow() {

    clearTimeout(this.hideTimer); // jic
    this.displayed = false;
    this.el.style.display = 'none';

    if (this.outsideEditInputClickEventRegistration !== null) {
      this.outsideEditInputClickEventRegistration.cancel();
    }

    this.outsideEditInputClickEventRegistration = null;

  }

  hide() {

    this.hideTimer = setTimeout(() => {

      this.hideNow();

    }, 2000);

  }

  downloadVideo(mime, filename, chunks) {
    const blob = new Blob(chunks, { 'type': mime });
    const videoURL = URL.createObjectURL(blob);
    const tag = document.createElement('a');
    tag.href = videoURL;
    tag.download = filename;
    document.body.appendChild(tag);
    tag.click();
    document.body.removeChild(tag);
  }

  getAudioVideoStreams() {

    const canvasStream = this.canvas.fabricCanvas.getElement().captureStream(24);
    const combinedStream = new MediaStream(canvasStream.getVideoTracks());
    const audioStreams = [];

    this.canvas.fabricCanvas.getObjects().forEach((object) => {
      if (object.type === 'audio' && object.audioElement) {
        const audioStream = object.audioElement.captureStream().getAudioTracks();
        audioStreams.push(...audioStream);
      }
      // if (object.type === 'video' && object.videoElement) {
      //   const audioStream = object.videoElement.captureStream().getAudioTracks();
      //   audioStreams.push(...audioStream);
      // }
    });

    audioStreams.forEach((audioTrack) => {
      combinedStream.addTrack(audioTrack);
    });

    return combinedStream;

  }

  getPlayerProgress() {

    const { durationInSeconds } = this.playerProperties;

    const elapsedTime = (performance.now() - this.startTime) / 1000;

    return (elapsedTime / durationInSeconds) * 100;

  }

  exportToWebM() {

    this.recordingProgressDisplay = new ProgressDisplay();

    mount(this.container, this.recordingProgressDisplay);

    const mime = 'video/webm';
    const filename = 'video.webm';
    this.stop();
    const combinedStream = this.getAudioVideoStreams();
    const mediaRecorder = new MediaRecorder(combinedStream, { mimeType: mime });

    let chunks = [];

    mediaRecorder.onstop = () => {
      this.downloadVideo(mime, filename, chunks);
      chunks = [];
    };

    mediaRecorder.ondataavailable = function(e) {
      chunks.push(e.data);
    };

    mediaRecorder.start();
    this.onEnd = () => {
      mediaRecorder.stop();
      this.onEnd = null;
      unmount(this.container, this.recordingProgressDisplay);
      this.recordingProgressDisplay = null;
    };
    this.play();

  }

  exportToMP4() {

    this.recordingProgressDisplay = new ProgressDisplay();

    mount(this.container, this.recordingProgressDisplay);

    const mime = 'video/mp4';
    const filename = 'video.mp4';
    this.stop();

    const combinedStream = this.getAudioVideoStreams();
    const mediaRecorder = new MediaRecorder(combinedStream, { mimeType: mime });

    let chunks = [];

    mediaRecorder.onstop = () => {
      this.downloadVideo(mime, filename, chunks);
      chunks = [];
    };

    mediaRecorder.ondataavailable = function(e) {
      chunks.push(e.data);
    };

    mediaRecorder.start();
    this.onEnd = () => {
      mediaRecorder.stop();
      this.onEnd = null;
      unmount(this.container, this.recordingProgressDisplay);
      this.recordingProgressDisplay = null;
    };
    this.play();

  }

  onunmount() {
    this.stop();
  }

  handleNameClick(track) {

    this.stop();

    const canvas = this.canvas.fabricCanvas;
    const object = canvas.getObjects().map((obj) => {
      obj.visible = false;
      return obj;
    }).find((obj) => obj.id === track.id);
    if (!object) return;

    object.visible = true;

    canvas.requestRenderAll();

  }

  update(properties) {

    const existing = {
      durationInSeconds: this.playerProperties.durationInSeconds,
      tracks: [...this.playerProperties.tracks],
    };
    const update = {
      durationInSeconds: properties.player.durationInSeconds,
      tracks: [...properties.player.tracks],
    };

    if (isEqual(existing, update) === true) {
      return;
    }

    this.stop();

    this.element.properties = structuredClone(properties);

    this.playerProperties = {
      durationInSeconds: this.element.properties.player.durationInSeconds,
      tracks: [...this.element.properties.player.tracks],
    };

    this.tracks.update(this.playerProperties.tracks);
    this.playerTimeline.update(0);
    this.playerDuration.update(0);

  }
}

export default VideoBuilder;